import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Users, Power, ClipboardCheck, FileText,
    BarChart3, Activity, Megaphone, Mail, BookOpen,
} from "lucide-react";

/**
 * Every destination in the admin console, grouped the way the rail shows them:
 *   Manage   — the four sections that live inside Admin.js itself
 *   Insights — read-only reporting pages
 *   Tools    — everything else
 *
 * The four "Manage" entries also carry the `title`/`subtitle` that Admin.js
 * renders as its page heading, since one component serves all four. The other
 * five are standalone pages that own their headings (some are built from live
 * data, e.g. Traffic's visit count), so they deliberately have none here.
 */
export const ADMIN_NAV = [
    {
        label: "Manage",
        items: [
            { key: "users",     label: "Users",     icon: Users,          path: "/admin/users",     title: "Users",                subtitle: "Manage roles and platform access" },
            { key: "sections",  label: "Sections",  icon: Power,          path: "/admin/sections",  title: "Section Availability", subtitle: "Turn the scheduler and platform sections on or off" },
            { key: "reviewers", label: "Reviewers", icon: ClipboardCheck, path: "/admin/reviewers", title: "Reviewer Whitelist",   subtitle: "Who can be invited to peer review" },
            { key: "showcase",  label: "Showcase",  icon: FileText,       path: "/admin/showcase",  title: "Showcase Papers",      subtitle: "Publicly shareable paper analyses" },
        ],
    },
    {
        label: "Insights",
        items: [
            { key: "analytics", label: "Analytics", icon: BarChart3, path: "/admin/analytics" },
            { key: "activity",  label: "Activity",  icon: Activity,  path: "/admin/activity" },
            { key: "traffic",   label: "Traffic",   icon: Megaphone, path: "/admin/traffic" },
        ],
    },
    {
        label: "Tools",
        items: [
            { key: "outreach", label: "Outreach", icon: Mail,     path: "/admin/outreach" },
            { key: "guide",    label: "Guide",    icon: BookOpen, path: "/admin/guide" },
        ],
    },
];

const ALL_ITEMS = ADMIN_NAV.flatMap(g => g.items);

// Falls back to Users so an unrecognised section key can never blank the page.
export function adminNavItem(key) {
    return ALL_ITEMS.find(i => i.key === key) ?? ALL_ITEMS[0];
}

/**
 * The console's section navigation: a grouped rail beside the content on
 * desktop, and the same items as one scrollable row of underline tabs at
 * narrow widths (the group headings drop away there — see App.css).
 *
 * The active item is derived from the URL so no page has to pass it in; bare
 * /admin is the Users route's alias, and the drill-down detail pages
 * (/admin/user, /admin/submission) match nothing and show no highlight.
 */
export default function AdminNav() {
    const { pathname } = useLocation();
    const activeRef = useRef(null);

    // React Router matches "/admin/users/" as well as "/admin/users", so trim any
    // trailing slash before comparing or a bookmarked URL highlights nothing.
    const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    const activePath = trimmed === "/admin" ? "/admin/users" : trimmed;

    // Collapsed into a scrollable tab row, the strip starts at scrollLeft 0, so a
    // page late in the order (Guide, Outreach) would open with its tab off-screen
    // and nothing appearing selected. Only scrolls when the row actually overflows.
    useEffect(() => {
        const el = activeRef.current;
        if (el && el.parentElement) {
            const rail = el.closest(".admin-rail");
            if (rail && rail.scrollWidth > rail.clientWidth) {
                el.scrollIntoView({ block: "nearest", inline: "center" });
            }
        }
    }, [activePath]);

    return (
        <nav className="admin-rail" aria-label="Admin console sections">
            {ADMIN_NAV.map(group => (
                <div
                    className="admin-rail-group"
                    key={group.label}
                    role="group"
                    aria-labelledby={`admin-rail-${group.label}`}
                >
                    <p className="admin-rail-group-label" id={`admin-rail-${group.label}`}>{group.label}</p>
                    {group.items.map(item => {
                        const Icon = item.icon;
                        const isActive = activePath === item.path;
                        return (
                            <Link
                                key={item.key}
                                ref={isActive ? activeRef : undefined}
                                to={item.path}
                                className={`admin-rail-item${isActive ? " admin-rail-item-active" : ""}`}
                                aria-current={isActive ? "page" : undefined}
                            >
                                <Icon size={16} className="admin-rail-icon" />
                                {item.label}
                            </Link>
                        );
                    })}
                </div>
            ))}
        </nav>
    );
}
